import { Badge, Box, Skeleton, Stack, Table, Text } from "@mantine/core";
import { useLayoutEffect, useRef, useState } from "react";
import { DATE_FORMAT } from "../../../constants/app-constant";
import { gray } from "../../../constants/color-constants";
import { EASTERN_TIME_ZONE } from "../../../constants/opcd-constants";
import { useOpcdCodingAlerts } from "../../../hooks/queries";
import { useAppSelector } from "../../../store/hooks";
import { formatDate } from "../../../utils";
import { encoderErrorMessage } from "../../../utils/encoder";
import { flattenProFeeAuditRows } from "../../../utils/pro-fee-audit";
import PollingRefreshIndicator from "../../Shared/PollingRefreshIndicator";

type BadgeTone = {
  background: string;
  border: string;
  text: string;
};

const SCOPE_BADGE_TONE: BadgeTone = {
  background: "#FFFBEB",
  border: "#FDE68A",
  text: "#B45309",
};

const TYPE_BADGE_TONE: BadgeTone = {
  background: "#F1F5F9",
  border: "#CBD5E1",
  text: "#1E293B",
};

const badgePropsFromTone = (
  tone: BadgeTone,
  options?: { wrap?: boolean; maxWidth?: number | "none" }
) => ({
  variant: "light" as const,
  styles: {
    root: {
      backgroundColor: tone.background,
      border: `1px solid ${tone.border}`,
      maxWidth: options?.maxWidth ?? "none",
      height: "auto",
    },
    label: {
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: options?.wrap ? ("normal" as const) : ("nowrap" as const),
      lineHeight: 1.35,
      color: tone.text,
      WebkitFontSmoothing: "antialiased" as const,
      overflowWrap: options?.wrap ? ("anywhere" as const) : undefined,
    },
  },
});

const kindBadgeProps = () => badgePropsFromTone(TYPE_BADGE_TONE);

const scopeBadgeProps = () =>
  badgePropsFromTone(SCOPE_BADGE_TONE, { wrap: true, maxWidth: 260 });

const headerCellStyle = {
  backgroundColor: gray[2],
  padding: "6px 16px",
  borderBottom: "2px solid #dee2e6",
  fontSize: "14px",
  fontWeight: 600,
  textAlign: "left" as const,
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  position: "sticky" as const,
  top: 0,
  zIndex: 5,
};

const cellStyle = (backgroundColor: string) => ({
  padding: "10px 16px",
  verticalAlign: "top" as const,
  backgroundColor,
});

const TABLE_MIN_WIDTH_WIDE = 980;
const TYPE_COLUMN_WIDTH = 200;

const OpcdCodingAlerts: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mergeTypeWithScope, setMergeTypeWithScope] = useState(false);
  const mrn = useAppSelector((state) => state.fhir.mrn);
  const { data, isLoading, isFetching, isError, error } = useOpcdCodingAlerts({
    polling: true,
  });
  const rows = flattenProFeeAuditRows(data?.audits);
  const requestError =
    data && data.success === false
      ? data.error || "Unable to load coding alerts"
      : isError
        ? encoderErrorMessage(error, "Unable to load coding alerts")
        : "";

  const showsTable =
    !isLoading &&
    Boolean(mrn) &&
    !(Boolean(requestError) && rows.length === 0);

  useLayoutEffect(() => {
    if (!showsTable) return;

    const el = scrollRef.current;
    if (!el) return;

    const syncCompactLayout = () => {
      setMergeTypeWithScope(el.clientWidth < TABLE_MIN_WIDTH_WIDE);
    };

    syncCompactLayout();
    const observer = new ResizeObserver(syncCompactLayout);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showsTable]);

  if (isLoading) {
    return (
      <Stack p="md" gap="sm">
        <Skeleton height={42} radius="md" />
        <Skeleton height={48} radius="md" />
        <Skeleton height={48} radius="md" />
      </Stack>
    );
  }

  if (!mrn) {
    return (
      <Box p="xl">
        <Text c="dimmed" ta="center">
          Encounter MRN is required to load coding alerts.
        </Text>
      </Box>
    );
  }

  if (requestError && !rows.length) {
    return (
      <Box p="xl">
        <Text c="red" ta="center">
          {requestError}
        </Text>
      </Box>
    );
  }

  return (
    <Stack
      p="sm"
      gap="sm"
      style={{ height: "100%", minHeight: 0, overflow: "hidden" }}
    >
      {requestError ? (
        <Text size="sm" c="red" role="alert">
          {requestError}
        </Text>
      ) : null}

      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          style={{
            pointerEvents: "none",
            position: "absolute",
            right: 12,
            top: 8,
            zIndex: 10,
          }}
        >
          <PollingRefreshIndicator visible={isFetching && !isLoading} />
        </Box>
        <Box
          ref={scrollRef}
          style={{
            overflowX: "auto",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            backgroundColor: gray[0],
          }}
        >
        <Table
          highlightOnHover
          highlightOnHoverColor={gray[0]}
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: mergeTypeWithScope
              ? TABLE_MIN_WIDTH_WIDE - TYPE_COLUMN_WIDTH
              : TABLE_MIN_WIDTH_WIDE,
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                style={{
                  ...headerCellStyle,
                  width: 38,
                  padding: "6px 2px",
                  textAlign: "center",
                }}
              >
                #
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 180 }}>
                {mergeTypeWithScope ? "Status/Scope · Type" : "Status/Scope"}
              </Table.Th>
              <Table.Th style={{ ...headerCellStyle, width: 180 }}>
                Codes
              </Table.Th>
              <Table.Th style={headerCellStyle}>Details</Table.Th>
              {!mergeTypeWithScope ? (
                <Table.Th
                  style={{
                    ...headerCellStyle,
                    width: 200,
                    minWidth: 200,
                    whiteSpace: "nowrap",
                  }}
                >
                  Type
                </Table.Th>
              ) : null}
              <Table.Th style={{ ...headerCellStyle, width: 150 }}>
                Date of Service
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!rows.length ? (
              <Table.Tr>
                <Table.Td colSpan={mergeTypeWithScope ? 5 : 6} py="xl">
                  <Text size="sm" c="dimmed" ta="center">
                    No audit alerts
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row, index) => {
                const backgroundColor = index % 2 === 0 ? "#FFFFFF" : gray[0];
                return (
                  <Table.Tr
                    key={row.key}
                    style={{ backgroundColor }}
                  >
                    <Table.Td
                      style={{
                        ...cellStyle(backgroundColor),
                        width: 38,
                        padding: "10px 2px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <Text size="sm" fw={500} c={gray[8]}>
                        {index + 1}.
                      </Text>
                    </Table.Td>
                    <Table.Td
                      style={{
                        ...cellStyle(backgroundColor),
                        verticalAlign: "middle",
                      }}
                    >
                      <Stack gap={6} align="flex-start">
                        {row.badge ? (
                          <Badge radius="sm" size="md" {...scopeBadgeProps()}>
                            {row.badge}
                          </Badge>
                        ) : (
                          <Text size="sm" c={gray[6]}>
                            -
                          </Text>
                        )}
                        {mergeTypeWithScope ? (
                          <Badge radius="sm" size="md" {...kindBadgeProps()}>
                            {row.kindLabel}
                          </Badge>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td
                      style={{
                        ...cellStyle(backgroundColor),
                        verticalAlign: "middle",
                      }}
                    >
                      <Text
                        size="sm"
                        fw={600}
                        c={gray[9]}
                        style={{ overflowWrap: "anywhere" }}
                      >
                        {row.codes || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td style={cellStyle(backgroundColor)}>
                      <Text
                        size="sm"
                        c={gray[8]}
                        style={{ overflowWrap: "anywhere" }}
                      >
                        {row.detail || "-"}
                      </Text>
                    </Table.Td>
                    {!mergeTypeWithScope ? (
                      <Table.Td
                        style={{
                          ...cellStyle(backgroundColor),
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                          width: 200,
                          minWidth: 200,
                        }}
                      >
                        <Badge radius="sm" size="md" {...kindBadgeProps()}>
                          {row.kindLabel}
                        </Badge>
                      </Table.Td>
                    ) : null}
                    <Table.Td
                      style={{
                        ...cellStyle(backgroundColor),
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Text size="sm" fw={500} c={gray[8]}>
                        {formatDate(
                          row.dateOfService || "",
                          DATE_FORMAT.MM_DD_YYYY,
                          EASTERN_TIME_ZONE
                        ) || "-"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
        </Box>
      </Box>
    </Stack>
  );
};

export default OpcdCodingAlerts;
